// packages/agent-kit/src/providers/PersonalizationProvider.tsx
import React, { createContext, useContext, useReducer, PropsWithChildren, useMemo } from 'react';
import type { AgentVersionCopyOverrides, AgentVersionBrandOverrides } from '@dopomogai/types';

type PersonalizationState = {
  heroTitle: string;
  heroSubtitle: string;
  bullets: string[];
  logoUrl: string;
  primaryColor: string;
};

type Action =
  | { type: 'SET_ALL'; payload: Partial<AgentVersionCopyOverrides & AgentVersionBrandOverrides> }
  | { type: 'SET_HERO_TITLE'; payload: string }
  | { type: 'SET_HERO_SUBTITLE'; payload: string }
  | { type: 'SET_BULLET'; payload: { text: string; index: number } }
  | { type: 'SET_LOGO_URL'; payload: string }
  | { type: 'SET_PRIMARY_COLOR'; payload: string };

const initialState: PersonalizationState = {
  heroTitle: '',
  heroSubtitle: '',
  bullets: [],
  logoUrl: '',
  primaryColor: '',
};

function sanitize(overrides?: Partial<AgentVersionCopyOverrides & AgentVersionBrandOverrides>): PersonalizationState {
  return {
    heroTitle: (overrides?.heroTitle ?? '') as string,
    heroSubtitle: (overrides?.heroSubtitle ?? '') as string,
    bullets: Array.isArray(overrides?.bullets) ? overrides!.bullets!.map(b => b ?? '') : [],
    logoUrl: (overrides?.logoUrl ?? '') as string,
    primaryColor: (overrides?.primaryColor ?? '') as string,
  };
}

function reducer(state: PersonalizationState, action: Action): PersonalizationState {
  switch (action.type) {
    case 'SET_ALL': {
      const next = sanitize(action.payload);
      // Only overwrite provided keys; keep existing for undefined
      return { ...state, ...Object.fromEntries(Object.entries(next).filter(([, v]) => v !== undefined)) };
    }
    case 'SET_HERO_TITLE':
      return { ...state, heroTitle: action.payload };
    case 'SET_HERO_SUBTITLE':
      return { ...state, heroSubtitle: action.payload };
    case 'SET_BULLET': {
      const { index, text } = action.payload;
      const bullets = [...(state.bullets || [])];
      while (bullets.length <= index) bullets.push('');
      bullets[index] = text;
      return { ...state, bullets };
    }
    case 'SET_LOGO_URL':
      return { ...state, logoUrl: action.payload };
    case 'SET_PRIMARY_COLOR':
      return { ...state, primaryColor: action.payload };
    default:
      return state;
  }
}

interface PersonalizationContextValue {
  state: PersonalizationState;
  dispatch: React.Dispatch<Action>;
}

const PersonalizationContext = createContext<PersonalizationContextValue | undefined>(undefined);

type ProviderProps = PropsWithChildren<{
  initialState?: Partial<AgentVersionCopyOverrides & AgentVersionBrandOverrides>;
}>;

export function PersonalizationProvider({ initialState: initialOverrides = {}, children }: ProviderProps) {
  const [state, dispatch] = useReducer(reducer, { ...initialState, ...sanitize(initialOverrides) });
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <PersonalizationContext.Provider value={value}>{children}</PersonalizationContext.Provider>;
}

export function usePersonalization() {
  const context = useContext(PersonalizationContext);
  if (context === undefined) {
    throw new Error('usePersonalization must be used within a PersonalizationProvider');
  }
  return context;
}